import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import BopRowActions, { RowIcon } from '../../../components/BopRowActions';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { useSocieta } from '../../../context/SocietaContext';
import { API_DOCS } from './ProdottiPage.api';

interface Prodotto {
  id: number;
  societaId: number;
  codice: string;
  nome: string;
  descrizione: string | null;
  attivo: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: { articoli: number };
}

interface ModalData {
  id: number | null;
  societaId: string;
  codice: string;
  nome: string;
  descrizione: string;
  attivo: boolean;
}

const EMPTY_MODAL: ModalData = {
  id: null,
  societaId: '',
  codice: '',
  nome: '',
  descrizione: '',
  attivo: true,
};

// Icona "Riattiva" (power-on)
const PowerIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
    <line x1="12" y1="2" x2="12" y2="12" />
  </svg>
);

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export default function ProdottiPage() {
  const { societaId: filterSocietaId, listaSocieta, isUnica, pronta } = useFiltroSocieta();
  const { can } = usePermesso();
  const { societaAttiva } = useSocieta();

  const canEdit = can('anagrafiche.prodotti', 'edit');
  const canDelete = can('anagrafiche.prodotti', 'delete');

  const [prodotti, setProdotti] = useState<Prodotto[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [mostraTutti, setMostraTutti] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState<ModalData>(EMPTY_MODAL);
  const [modalError, setModalError] = useState('');
  const [saving, setSaving] = useState(false);

  const caricaLista = useCallback(async () => {
    if (!pronta) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterSocietaId) params.set('societaId', filterSocietaId);
      if (mostraTutti) params.set('mostraTutti', '1');
      if (search.trim()) params.set('search', search.trim());
      // @api GET /api/anagrafiche/prodotti -> {"success":true,"data":[{"id":1,"codice":"...","nome":"...","attivo":true}]}
      const res = await apiFetch<{ success: boolean; data: Prodotto[] }>(`/api/anagrafiche/prodotti?${params}`);
      setProdotti(res.data || []);
    } catch (err) {
      console.error('[ProdottiPage] caricaLista', err);
      setProdotti([]);
    } finally {
      setLoading(false);
    }
  }, [pronta, filterSocietaId, mostraTutti, search]);

  useEffect(() => {
    if (pronta) caricaLista();
  }, [pronta, filterSocietaId, mostraTutti]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCerca = () => caricaLista();

  // ── Modal ──────────────────────────────────────────────────────────────

  const openNuovo = () => {
    const sid = filterSocietaId || (societaAttiva ? String(societaAttiva.id) : '');
    setModalData({ ...EMPTY_MODAL, societaId: sid });
    setModalError('');
    setShowModal(true);
  };

  const openModifica = (p: Prodotto) => {
    setModalData({
      id: p.id,
      societaId: String(p.societaId),
      codice: p.codice,
      nome: p.nome,
      descrizione: p.descrizione || '',
      attivo: p.attivo,
    });
    setModalError('');
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setModalError('');
  };

  const validaModal = (): string | null => {
    if (!modalData.id && !modalData.societaId) return 'Società è obbligatoria';
    if (!modalData.codice.trim()) return 'Codice è obbligatorio';
    if (modalData.codice.trim().length > 50) return 'Codice massimo 50 caratteri';
    if (!modalData.nome.trim()) return 'Nome è obbligatorio';
    return null;
  };

  const salva = async () => {
    const err = validaModal();
    if (err) { setModalError(err); return; }

    setSaving(true);
    setModalError('');
    try {
      if (modalData.id) {
        // @api PUT /api/anagrafiche/prodotti/:id {"codice":"...","nome":"...","descrizione":"...","attivo":true} -> {"success":true,"data":{"id":1}}
        await apiFetch<{ success: boolean }>(`/api/anagrafiche/prodotti/${modalData.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            codice: modalData.codice.trim(),
            nome: modalData.nome.trim(),
            descrizione: modalData.descrizione.trim() || null,
            attivo: modalData.attivo,
          }),
        });
      } else {
        // @api POST /api/anagrafiche/prodotti {"societaId":1,"codice":"...","nome":"...","descrizione":"..."} -> {"success":true,"data":{"id":1}}
        await apiFetch<{ success: boolean; data: { id: number } }>('/api/anagrafiche/prodotti', {
          method: 'POST',
          body: JSON.stringify({
            societaId: parseInt(modalData.societaId, 10),
            codice: modalData.codice.trim(),
            nome: modalData.nome.trim(),
            descrizione: modalData.descrizione.trim() || null,
          }),
        });
      }
      setShowModal(false);
      await caricaLista();
    } catch (e: any) {
      setModalError(e?.message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const disattiva = async (p: Prodotto) => {
    if (!window.confirm(`Disattivare il prodotto "${p.codice} — ${p.nome}"?`)) return;
    try {
      // @api DELETE /api/anagrafiche/prodotti/:id -> {"success":true}
      await apiFetch<{ success: boolean }>(`/api/anagrafiche/prodotti/${p.id}`, { method: 'DELETE' });
      await caricaLista();
    } catch (e) {
      console.error('[ProdottiPage] disattiva', e);
    }
  };

  const riattiva = async (p: Prodotto) => {
    try {
      // @api PUT /api/anagrafiche/prodotti/:id {"attivo":true} -> {"success":true}
      await apiFetch<{ success: boolean }>(`/api/anagrafiche/prodotti/${p.id}`, {
        method: 'PUT',
        body: JSON.stringify({ attivo: true }),
      });
      await caricaLista();
    } catch (e) {
      console.error('[ProdottiPage] riattiva', e);
    }
  };

  return (
    <Layout>
      <div className="prodotti-page">
        <div className="bop-page-header">
          <div>
            <h1 className="bop-page-title">Prodotti</h1>
            <p className="bop-text-muted">
              Anagrafica prodotti SaaS/servizi collegati ad articoli ricorrenti
            </p>
          </div>
          {canEdit && (
            <button className="bop-btn-primary" onClick={openNuovo}>
              + Nuovo prodotto
            </button>
          )}
        </div>

        {/* Filtri */}
        <div className="bop-card">
          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Ricerca (codice o nome)</label>
              <input
                type="text"
                className="bop-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCerca(); }}
                placeholder="Cerca per codice o nome..."
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">&nbsp;</label>
              <label className="bop-flex">
                <input
                  type="checkbox"
                  checked={mostraTutti}
                  onChange={(e) => setMostraTutti(e.target.checked)}
                />
                <span>Mostra anche disattivati</span>
              </label>
            </div>
            <div className="bop-form-group">
              <label className="bop-label">&nbsp;</label>
              <button className="bop-btn-secondary" onClick={handleCerca} disabled={loading}>
                {loading ? 'Ricerca...' : 'Cerca'}
              </button>
            </div>
          </div>
        </div>

        {/* Tabella */}
        <div className="bop-card">
          <table className="bop-table">
            <thead>
              <tr>
                <th>Codice</th>
                <th>Nome</th>
                <th>Descrizione</th>
                <th className="bop-text-right">Articoli associati</th>
                <th>Attivo</th>
                <th className="bop-text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6}>
                    <div className="bop-empty-state">Caricamento...</div>
                  </td>
                </tr>
              ) : prodotti.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <div className="bop-empty-state">Nessun prodotto trovato</div>
                  </td>
                </tr>
              ) : (
                prodotti.map(p => (
                  <tr key={p.id}>
                    <td>{p.codice}</td>
                    <td>{p.nome}</td>
                    <td className="prodotti-descrizione-cell">
                      {p.descrizione || <span className="bop-text-muted">—</span>}
                    </td>
                    <td className="bop-text-right">
                      {p._count?.articoli ?? 0}
                    </td>
                    <td>
                      {p.attivo
                        ? <span className="bop-badge bop-badge-success">Attivo</span>
                        : <span className="bop-badge bop-badge-danger">Disattivato</span>}
                    </td>
                    <td className="bop-text-right">
                      <BopRowActions actions={[
                        {
                          icon: <RowIcon.Edit />,
                          label: 'Modifica',
                          onClick: () => openModifica(p),
                          variant: 'primary',
                          hidden: !canEdit,
                        },
                        {
                          icon: <RowIcon.Disable />,
                          label: 'Disattiva',
                          onClick: () => disattiva(p),
                          variant: 'danger',
                          hidden: !canDelete || !p.attivo,
                        },
                        {
                          icon: <PowerIcon />,
                          label: 'Riattiva',
                          onClick: () => riattiva(p),
                          variant: 'primary',
                          hidden: !canEdit || p.attivo,
                        },
                      ]} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Modal nuovo/modifica — niente chiusura su click esterno (regola BOP) */}
        {showModal && (
          <div className="bop-modal-overlay">
            <div className="bop-modal" onClick={(e) => e.stopPropagation()}>
              <div className="bop-modal__header">
                <h3 className="bop-modal__title">
                  {modalData.id ? 'Modifica prodotto' : 'Nuovo prodotto'}
                </h3>
                <button
                  type="button"
                  className="bop-modal__close"
                  onClick={closeModal}
                  disabled={saving}
                  aria-label="Chiudi"
                >
                  <XIcon />
                </button>
              </div>

              <div className="bop-modal__body">
                {modalError && (
                  <div className="bop-text-danger">{modalError}</div>
                )}

                {/* Società — solo creazione, solo se non monosocietà */}
                {!modalData.id && !isUnica && (
                  <div className="bop-form-group">
                    <label className="bop-label">
                      Società <span className="bop-text-danger">*</span>
                    </label>
                    <select
                      className="bop-select"
                      value={modalData.societaId}
                      onChange={(e) => setModalData({ ...modalData, societaId: e.target.value })}
                      disabled={saving}
                    >
                      <option value="">Seleziona società...</option>
                      {listaSocieta.map(s => (
                        <option key={s.id} value={s.id}>{s.nome}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="bop-form-group">
                  <label className="bop-label">
                    Codice <span className="bop-text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="bop-input"
                    maxLength={50}
                    value={modalData.codice}
                    onChange={(e) => setModalData({ ...modalData, codice: e.target.value })}
                    placeholder="Es. SMARTILIO"
                    disabled={saving}
                  />
                </div>

                <div className="bop-form-group">
                  <label className="bop-label">
                    Nome <span className="bop-text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="bop-input"
                    value={modalData.nome}
                    onChange={(e) => setModalData({ ...modalData, nome: e.target.value })}
                    placeholder="Nome del prodotto"
                    disabled={saving}
                  />
                </div>

                <div className="bop-form-group">
                  <label className="bop-label">Descrizione</label>
                  <textarea
                    className="bop-input"
                    rows={3}
                    value={modalData.descrizione}
                    onChange={(e) => setModalData({ ...modalData, descrizione: e.target.value })}
                    placeholder="Descrizione opzionale"
                    disabled={saving}
                  />
                </div>

                {modalData.id && (
                  <div className="bop-form-group">
                    <label className="bop-flex">
                      <input
                        type="checkbox"
                        checked={modalData.attivo}
                        onChange={(e) => setModalData({ ...modalData, attivo: e.target.checked })}
                        disabled={saving}
                      />
                      <span>Attivo</span>
                    </label>
                  </div>
                )}
              </div>

              <div className="bop-modal__footer">
                <button className="bop-btn-secondary" onClick={closeModal} disabled={saving}>
                  Annulla
                </button>
                <button className="bop-btn-primary" onClick={salva} disabled={saving}>
                  {saving ? 'Salvataggio...' : 'Salva'}
                </button>
              </div>
            </div>
          </div>
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
